import { Button, CheckIcon, EyeOffIcon, LockIcon, Paragraph, TextInput, notification } from "@datagame/ui";
import { Field, Form, Formik } from "formik";
import { object, string } from "yup";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { UserUpdatePasswordInput } from "../../../../gql/graphql";
import { USER_UPDATE_PASSWORD } from "../../../../graphql/mutations/UserMutations";
import { FieldWrapper } from "../../../shared/ui/FieldWrapper";
import { ErrorMessage } from "../../../shared/ui/ErrorMessage";
import { PASSWORD_PATTERN } from "../../../../constants";

import styles from "./PasswordForm.module.scss";

export interface PasswordFormProps {
  isActive: boolean;
  onSubmit: () => void;
}

export const PasswordForm = ({ isActive, onSubmit }: PasswordFormProps) => {
  const { t } = useTranslation();

  const [userPasswordUpdate, { error }] = useMutation(USER_UPDATE_PASSWORD);

  const validationSchema = object({
    oldPassword: string().required(),
    newPassword: string()
      .required()
      .min(8)
      .max(50)
      .matches(
        PASSWORD_PATTERN,
        "Password must contain at least one uppercase letter, one lowercase letter and one number",
      ),
  });

  const initialValues: UserUpdatePasswordInput = {
    oldPassword: "",
    newPassword: "",
  };

  const handleSubmit = (values: UserUpdatePasswordInput, { resetForm }: { resetForm: () => void }) => {
    const variables = {
      data: values,
    };

    userPasswordUpdate({
      variables,
      onCompleted: () => {
        notification.success({
          title: t("profile:passwordSection.form.successTitle"),
          description: t("profile:passwordSection.form.successMessage"),
        });

        resetForm();
        onSubmit();
      },
    });
  };

  if (!isActive) {
    return (
      <div className={styles.hiddenPlaceholder}>
        <EyeOffIcon className={styles.eyeIcon} />
        <div>
          <Paragraph color="neutrals-600" weight="font-bold">
            {t("profile:passwordSection.hidden")}
          </Paragraph>
          <Paragraph color="neutrals-600">{t("profile:passwordSection.editToShow")}</Paragraph>
        </div>
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ errors }) => (
        <Form className={styles.form}>
          {error && <ErrorMessage>{error.message}</ErrorMessage>}
          <FieldWrapper label={t("profile:passwordSection.form.oldPassword")} name="oldPassword">
            <Field
              placeholder={t("profile:passwordSection.form.oldPasswordPlaceholder")}
              name="oldPassword"
              size="small"
              as={TextInput}
              icon={<LockIcon />}
              type="password"
              error={errors.oldPassword}
            />
          </FieldWrapper>

          <FieldWrapper
            label={t("profile:passwordSection.form.newPassword")}
            name="newPassword"
            tip={t("profile:passwordSection.form.tip") ?? ""}
            tipClassName={styles.tip}
          >
            <Field
              placeholder={t("profile:passwordSection.form.newPasswordPlaceholder")}
              name="newPassword"
              size="small"
              as={TextInput}
              icon={<LockIcon />}
              type="password"
              error={errors.newPassword}
            />
          </FieldWrapper>

          <Button type="submit" icon={<CheckIcon />} iconSide="left" className={styles.submitButton}>
            {t("profile:change")}
          </Button>
        </Form>
      )}
    </Formik>
  );
};
